import {useEffect, useMemo, useRef} from 'react';
import {useShallow} from 'zustand/shallow';
import {z} from 'zod';
import useUserQuery from '@/hooks/queries/use-user-query';
import {STEPS, DEFAULT_STEP_ORDER, type Step} from './onboardingSteps';
import {useUserData} from '@/hooks/useUserData';
import {UserSchema} from '@/schema/User';
import Sentry from '@/services/sentry/Sentry';
import type {PortfolioMediaItem} from '@/types/portfolioMediaItem';
import {SocialPlatform} from '@/types/platforms';
import {
  useOnboardingStore,
  type GenderOption,
  ONBOARDING_STATE_EXPIRATION_TIME_MS,
} from './OnboardingStore';
import {isStepValid} from './useStepValidation';

function handleUserParseError(error: unknown) {
  if (error instanceof z.ZodError) {
    Sentry.captureException(error, {
      title: 'Onboarding User Parse Error',
    });
  } else {
    Sentry.captureException(error as any, {
      title: 'Unexpected Onboarding Parse Error',
    });
  }
}

/**
 * Determines if the user should use the reonboarding flow (vs normal onboarding flow).
 * This is used when the user is already in the onboarding process to determine flow type.
 *
 * Reonboarding flow is used when:
 * 1. User has incomplete steps
 * 2. User account is older than 24 hours
 *
 * Note: This is different from useReonboarding hook which determines if user should be
 * redirected TO onboarding. This function determines which flow to show once already in onboarding.
 */
function shouldUseReonboardingFlow(
  incompleteSteps: Step[],
  userCreatedAt: number | undefined
): boolean {
  const hasIncompleteSteps = incompleteSteps.length > 0;
  const isUserOldEnough =
    !!userCreatedAt &&
    Date.now() - userCreatedAt > ONBOARDING_STATE_EXPIRATION_TIME_MS;

  return hasIncompleteSteps && isUserOldEnough;
}

/**
 * Calculates which steps are incomplete based on current state.
 */
function calculateIncompleteSteps(
  state: ReturnType<typeof useOnboardingStore.getState>,
  connectedPlatforms: SocialPlatform[]
): Step[] {
  return DEFAULT_STEP_ORDER.filter(
    (step) => !isStepValid(step, state, connectedPlatforms)
  );
}

/**
 * Builds the final step order based on incomplete steps and flow type.
 * Assumes incompleteSteps is not empty - caller should handle the empty case.
 */
function buildStepOrder(
  incompleteSteps: Step[],
  isReonboarding: boolean
): Step[] {
  if (isReonboarding) {
    return [STEPS.REONBOARDING, ...incompleteSteps, STEPS.COMPLETION];
  }

  return [...incompleteSteps, STEPS.COMPLETION];
}

function useStepOrder() {
  const {
    user,
    isLoading: isUserLoading,
    isFetching: isUserFetching,
  } = useUserQuery({include: ['profile.profile_progress_report']});
  const {connectedPlatforms: rawConnectedPlatforms} = useUserData({});
  const hasInitializedStepOrder = useRef(false);

  // Memoize connectedPlatforms to prevent unnecessary effect reruns
  const connectedPlatforms = useMemo(
    () => rawConnectedPlatforms,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(rawConnectedPlatforms)]
  );

  const {
    hydrate,
    isHydrated,
    isStepOrderPersisted,
    setStepOrder,
    setStep,
    setFlowName,
    step: currentStep,
  } = useOnboardingStore(
    useShallow((state) => ({
      hydrate: state.hydrate,
      isHydrated: state.isHydrated,
      isStepOrderPersisted: state.isStepOrderPersisted,
      setStepOrder: state.setStepOrder,
      setStep: state.setStep,
      setFlowName: state.setFlowName,
      step: state.step,
    }))
  );

  // Hydrate store with user data from backend
  useEffect(() => {
    // Don't hydrate if still loading/fetching - wait for fresh data
    if (isUserLoading || isUserFetching) return;
    if (!user || isHydrated) return;

    let validUser;
    try {
      validUser = UserSchema.parse(user);
    } catch (e) {
      handleUserParseError(e);
    }

    if (!validUser) return;

    // Determine segment
    let segment;
    if (validUser.profile?.influencer) {
      segment = 'influencer' as const;
    } else if (validUser.profile?.contentCreator) {
      segment = 'creator' as const;
    }

    // Normalize gender
    let gender: GenderOption | undefined;
    if (validUser.profile?.gender === 'Male') {
      gender = 'Male';
    } else if (validUser.profile?.gender === 'Female') {
      gender = 'Female';
    } else if (validUser.profile?.gender === 'Prefer not to say') {
      gender = 'Prefer not to say';
    } else if (typeof validUser.profile?.gender === 'string') {
      gender = 'Other';
    }

    // Get profile picture from profile or identity
    const profilePictureUrl: string | undefined = (validUser.profile
      ?.imageUrl ||
      validUser.identities?.find((identity) => identity.cachedProfileImageUrl)
        ?.cachedProfileImageUrl) as string | undefined;

    hydrate({
      firstName: validUser.firstName ?? '',
      lastName: validUser.lastName ?? '',
      birthdate: validUser.birthDate
        ? new Date(validUser.birthDate)
        : undefined,
      segment,
      gender,
      specialties: new Set(validUser.profile?.tags.map((tag) => tag.name)),
      capabilities: new Set(
        validUser.profile?.contentPreferences?.map(
          (capability) => capability.kind
        )
      ),
      bio: validUser.profile?.bio ? validUser.profile?.bio : undefined,
      profilePictureUrl,
      portfolioLink: validUser.profile?.portfolioLink ?? undefined,
      portfolioMediaItems: validUser.profile?.portfolioMediaItems
        .filter((item) => item.state !== 'processing')
        .map(
          (item) =>
            ({
              type: item.kind,
              url: item.url,
            } as PortfolioMediaItem)
        ),
      location: validUser.profile?.location,
      locale: validUser.profile?.locale,
      minCollaborationRate:
        validUser.profile?.minCollaborationRate ?? undefined,
      userCreatedAt: validUser.createdAt?.getTime() ?? undefined,
    });
  }, [user, isHydrated, hydrate, isUserLoading, isUserFetching]);

  // Initialize step order AFTER hydration is complete
  useEffect(() => {
    // Wait for hydration to complete before initializing step order
    if (!isHydrated) return;

    // Only initialize once per mount (unless forced via URL param)
    const urlParams = new URLSearchParams(window.location.search);
    const isAllSteps = urlParams.get('allSteps') === 'true';

    if (hasInitializedStepOrder.current && !isAllSteps) return;
    hasInitializedStepOrder.current = true;

    // Handle ?allSteps=true - show all steps (for testing)
    if (isAllSteps) {
      setStepOrder(DEFAULT_STEP_ORDER);
      setFlowName('onboarding');
      if (!DEFAULT_STEP_ORDER.includes(currentStep)) {
        setStep(DEFAULT_STEP_ORDER[0]);
      }
      return;
    }

    // If we have valid persisted state (not expired), use it
    // This preserves the user's progress when returning from social auth
    if (isStepOrderPersisted) {
      // Step order and current step are already restored from localStorage
      // Just need to ensure flowName is set if not already
      const state = useOnboardingStore.getState();
      if (!state.flowName) {
        // Determine flow name based on whether REONBOARDING step is in the order
        const hasReonboardingStep = state.stepOrder.includes(
          STEPS.REONBOARDING
        );
        setFlowName(hasReonboardingStep ? 'reonboarding' : 'onboarding');
      }
      return;
    }

    // No valid persisted state - calculate step order from scratch
    const state = useOnboardingStore.getState();

    // Calculate which steps are incomplete
    const incompleteSteps = calculateIncompleteSteps(state, connectedPlatforms);

    // Edge case: all steps are already complete
    // This shouldn't happen in practice (user wouldn't be in onboarding)
    // but handle gracefully by showing completion
    if (incompleteSteps.length === 0) {
      setFlowName('onboarding');
      setStepOrder([STEPS.COMPLETION]);
      setStep(STEPS.COMPLETION);
      return;
    }

    // Determine if this is a reonboarding flow
    const isReonboarding = shouldUseReonboardingFlow(
      incompleteSteps,
      state.userCreatedAt
    );

    // Build the step order
    const newStepOrder = buildStepOrder(incompleteSteps, isReonboarding);

    // Set flow name first (for analytics)
    setFlowName(isReonboarding ? 'reonboarding' : 'onboarding');

    // Set the step order
    setStepOrder(newStepOrder);

    if (isReonboarding) {
      setStep(STEPS.REONBOARDING);
      return;
    }

    // Navigate to the first step in the order if current step is not valid
    if (!newStepOrder.includes(currentStep)) {
      setStep(newStepOrder[0]);
    }
  }, [
    isHydrated,
    isStepOrderPersisted,
    connectedPlatforms,
    setStepOrder,
    setStep,
    setFlowName,
    currentStep,
  ]);
}

export default useStepOrder;
